import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Phone, Mail, Clock, DollarSign, User, PawPrint, CreditCard, CheckCircle, XCircle, Loader2, AlertTriangle, Calendar, CalendarPlus } from 'lucide-react';
import LoadingSpinner from '../../LoadingSpinner';
import {
    createCheckout,
    waitForCheckout,
    checkHealth,
    CHECKOUT_STATUS,
    SQUARE_LOCATION_IDS,
    SQUARE_DEVICE_IDS,
} from '../../services/squarePaymentsService';
import { getUpcomingAppointments } from '../../services/daysmartService';

const AUTO_CLOSE_SECONDS = 60;

const AppointmentDetailsModal = ({ isOpen, onClose, ticket, loading, error, onPaymentComplete, currentUserId, viewOnly = false, onCallClient, onCancelAppointment }) => {
    const [paymentState, setPaymentState] = useState('idle'); // idle, processing, success, error
    const [paymentError, setPaymentError] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [serverHealthy, setServerHealthy] = useState(null); // null = checking, true = healthy, false = unhealthy
    const [serverError, setServerError] = useState(null);
    const [upcomingAppointments, setUpcomingAppointments] = useState([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [autoCloseCountdown, setAutoCloseCountdown] = useState(null);
    const autoCloseTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    // Cancel appointment state
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelComment, setCancelComment] = useState('');
    const [cancelState, setCancelState] = useState('idle'); // idle, cancelling, success, error
    const [cancelError, setCancelError] = useState(null);

    // Clear auto-close timers
    const clearAutoCloseTimers = useCallback(() => {
        if (autoCloseTimerRef.current) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setAutoCloseCountdown(null);
    }, []);

    // Start auto-close timer after successful payment
    const startAutoCloseTimer = useCallback(() => {
        clearAutoCloseTimers();
        setAutoCloseCountdown(AUTO_CLOSE_SECONDS);
        
        // Countdown interval
        countdownIntervalRef.current = setInterval(() => {
            setAutoCloseCountdown(prev => {
                if (prev <= 1) {
                    clearAutoCloseTimers();
                    onClose();
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
    }, [clearAutoCloseTimers, onClose]);

    // Reset payment state and check server health when modal opens
    useEffect(() => {
        if (isOpen) {
            // Reset payment state for new appointment
            setPaymentState('idle');
            setPaymentError(null);
            setPaymentResult(null);
            setServerHealthy(null);
            setServerError(null);
            setUpcomingAppointments([]);
            setUpcomingLoading(false);
            clearAutoCloseTimers();
            setShowCancelConfirm(false);
            setCancelComment('');
            setCancelState('idle');
            setCancelError(null);
            checkHealth().then((result) => {
                setServerHealthy(result.healthy);
                if (!result.healthy) {
                    setServerError(result.error || 'Square payments server is unavailable');
                }
            });
        }
        
        // Cleanup on unmount
        return () => {
            clearAutoCloseTimers();
        };
    }, [isOpen, clearAutoCloseTimers]);

    if (!isOpen) return null;

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    const getLocationName = (locationId) => {
        return locationId === 1 ? 'Hopkins' : locationId === 2 ? 'Inver Grove' : 'Unknown';
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            0: 'Scheduled',
            1: 'Checked In',
            2: 'In Progress',
            3: 'Completed',
            4: 'Cancelled',
        };
        return statusMap[status] || 'Unknown';
    };

    const getStatusColor = (status) => {
        const colorMap = {
            0: 'bg-blue-100 text-blue-800',
            1: 'bg-yellow-100 text-yellow-800',
            2: 'bg-purple-100 text-purple-800',
            3: 'bg-green-100 text-green-800',
            4: 'bg-red-100 text-red-800',
        };
        return colorMap[status] || 'bg-gray-100 text-gray-800';
    };

    // Calculate totals
    const calculateTotals = () => {
        if (!ticket?.items) return { subtotal: 0, backbar: 0, tax: 0, total: 0 };
        
        const subtotal = ticket.items.reduce((sum, item) => sum + (item.row_subtotal || 0), 0);
        const backbar = ticket.items.reduce((sum, item) => sum + (item.back_bar || 0), 0);
        const total = ticket.ticket_total || subtotal;
        // Tax is the difference between total and subtotal (DaySmart doesn't return tax separately)
        const tax = Math.max(0, total - subtotal);
        
        return {
            subtotal,
            backbar,
            tax,
            total
        };
    };

    const totals = calculateTotals();

    // Handle Pay Now button click
    const handlePayNow = async () => {
        if (!ticket || paymentState === 'processing') return;

        const locationId = ticket.location_id;
        const squareLocationId = SQUARE_LOCATION_IDS[locationId];
        const deviceId = SQUARE_DEVICE_IDS[locationId];

        if (!squareLocationId || !deviceId) {
            setPaymentState('error');
            setPaymentError(`No Square terminal configured for ${locationId === 1 ? 'Hopkins' : 'Inver Grove'}`);
            return;
        }

        // Convert total to cents (totals.total is in dollars)
        const amountCents = Math.round(totals.total * 100);
        if (amountCents <= 0) {
            setPaymentState('error');
            setPaymentError('Invalid payment amount');
            return;
        }

        setPaymentState('processing');
        setPaymentError(null);
        setPaymentResult(null);

        try {
            // Create the checkout on the terminal
            const checkout = await createCheckout({
                deviceId,
                locationId: squareLocationId,
                amountCents,
                referenceId: `ticket-${ticket.ticket_id}`,
                note: `${ticket.client_first_name} ${ticket.client_last_name}`,
            });

            // Wait for the customer to complete payment
            const result = await waitForCheckout(checkout.checkout_id, 120000);

            if (result.status === CHECKOUT_STATUS.COMPLETED) {
                setPaymentState('success');
                setPaymentResult({
                    checkoutId: result.checkout_id,
                    amountCents: result.amount_cents,
                    tipCents: result.tip_cents || 0,
                    totalCents: result.total_cents,
                    paymentIds: result.payment_ids,
                });

                // Notify parent component for DaySmart integration
                if (onPaymentComplete) {
                    onPaymentComplete({
                        ticketId: ticket.ticket_id,
                        locationId: ticket.location_id,
                        checkoutId: result.checkout_id,
                        amountCents: result.amount_cents,
                        tipCents: result.tip_cents || 0,
                        totalCents: result.total_cents,
                        paymentIds: result.payment_ids,
                    });
                }

                // Fetch upcoming appointments for the client
                if (ticket.client_id) {
                    setUpcomingLoading(true);
                    try {
                        const upcomingResult = await getUpcomingAppointments(ticket.client_id, ticket.location_id);
                        setUpcomingAppointments(upcomingResult.appointments || []);
                    } catch (upcomingErr) {
                        console.error('Failed to fetch upcoming appointments:', upcomingErr);
                    } finally {
                        setUpcomingLoading(false);
                    }
                }
                
                // Start auto-close timer after payment success
                startAutoCloseTimer();
            } else if (result.status === CHECKOUT_STATUS.CANCELED) {
                setPaymentState('error');
                setPaymentError('Payment was cancelled');
            } else if (result.timed_out) {
                setPaymentState('error');
                setPaymentError('Payment timed out. Please try again.');
            } else {
                setPaymentState('error');
                setPaymentError(`Payment failed: ${result.status}`);
            }
        } catch (err) {
            setPaymentState('error');
            setPaymentError(err.response?.data?.detail || err.message || 'Payment failed');
        }
    };

    // Handle cancel appointment
    const handleCancelAppointment = async () => {
        if (!onCancelAppointment || !ticket) return;
        setCancelState('cancelling');
        setCancelError(null);
        try {
            await onCancelAppointment(ticket.ticket_id, ticket.location_id, cancelComment);
            setCancelState('success');
        } catch (err) {
            setCancelState('error');
            setCancelError(err.message || 'Failed to cancel appointment');
        }
    };

    // Reset payment state when modal closes
    const handleClose = () => {
        clearAutoCloseTimers();
        setPaymentState('idle');
        setPaymentError(null);
        setPaymentResult(null);
        setUpcomingAppointments([]);
        setShowCancelConfirm(false);
        setCancelComment('');
        setCancelState('idle');
        setCancelError(null);
        onClose();
    };

    // Skip payment and go directly to upcoming appointments (for testing)
    const handleSkipPayment = async () => {
        setPaymentState('success');
        setPaymentResult({
            checkoutId: 'skipped',
            amountCents: 0,
            tipCents: 0,
            totalCents: 0,
            paymentIds: [],
        });

        // Fetch upcoming appointments for the client
        if (ticket.client_id) {
            setUpcomingLoading(true);
            try {
                const upcomingResult = await getUpcomingAppointments(ticket.client_id, ticket.location_id);
                setUpcomingAppointments(upcomingResult.appointments || []);
            } catch (upcomingErr) {
                console.error('Failed to fetch upcoming appointments:', upcomingErr);
            } finally {
                setUpcomingLoading(false);
            }
        }

        // Start auto-close timer
        startAutoCloseTimer();
    };

    // Check if terminal is available for this location
    const hasTerminal = ticket && SQUARE_DEVICE_IDS[ticket.location_id];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-purple-600 text-white px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold">Appointment Details</h2>
                        {ticket && (
                            <p className="text-purple-200 text-sm">
                                Ticket #{ticket.ticket_id} • {getLocationName(ticket.location_id)}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-purple-700 rounded-full p-1 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
                            <p className="font-medium">Error loading appointment details</p>
                            <p className="text-sm mt-1">{error}</p>
                        </div>
                    ) : ticket ? (
                        <div className="space-y-6">
                            {/* Client Info */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <User size={18} />
                                    Client Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-lg font-medium">
                                            {ticket.client_first_name} {ticket.client_last_name}
                                        </p>
                                        {ticket.client_cell_phone && (
                                            <p className="text-gray-600 flex items-center gap-2 mt-1">
                                                {onCallClient ? (
                                                    <button
                                                        onClick={() => onCallClient(ticket.client_cell_phone, {
                                                            clientId: ticket.client_id,
                                                            clientName: `${ticket.client_first_name} ${ticket.client_last_name}`.trim(),
                                                            locationId: ticket.location_id,
                                                        })}
                                                        className="text-green-600 hover:text-green-800 hover:bg-green-100 rounded p-0.5 transition-colors"
                                                        title="Call client"
                                                    >
                                                        <Phone size={14} />
                                                    </button>
                                                ) : (
                                                    <Phone size={14} />
                                                )}
                                                {ticket.client_cell_phone}
                                            </p>
                                        )}
                                        {ticket.client_email && (
                                            <p className="text-gray-600 flex items-center gap-2 mt-1">
                                                <Mail size={14} />
                                                {ticket.client_email}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ticket.ticket_status)}`}>
                                            {getStatusLabel(ticket.ticket_status)}
                                        </span>
                                        {ticket.confirmation_type && (
                                            <p className="text-gray-500 text-sm mt-2">
                                                Confirmation: {ticket.confirmation_type}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Appointment Time */}
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h3 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                                    <Clock size={18} />
                                    Scheduled Date
                                </h3>
                                <p className="text-lg">{formatDate(ticket.date_scheduled)}</p>
                            </div>

                            {/* Services */}
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <PawPrint size={18} />
                                    Services ({ticket.items?.length || 0})
                                </h3>
                                <div className="border rounded-lg overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Service</th>
                                                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Pet</th>
                                                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Groomer</th>
                                                <th className="text-left px-4 py-2 text-sm font-medium text-gray-600">Time</th>
                                                <th className="text-right px-4 py-2 text-sm font-medium text-gray-600">Price</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {ticket.items?.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <p className="font-medium">{item.name}</p>
                                                        <p className="text-sm text-gray-500">{item.category}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-gray-700">{item.pet_name || '-'}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-gray-700">
                                                            {item.employee_first_name 
                                                                ? `${item.employee_first_name} ${item.employee_last_name || ''}`.trim()
                                                                : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">
                                                        {item.start_datetime && (
                                                            <>
                                                                {formatTime(item.start_datetime)}
                                                                {item.end_datetime && item.end_datetime !== item.start_datetime && (
                                                                    <> - {formatTime(item.end_datetime)}</>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="font-medium">{formatCurrency(item.row_total)}</span>
                                                        {item.discount_currency > 0 && (
                                                            <p className="text-xs text-green-600">
                                                                -{formatCurrency(item.discount_currency)} discount
                                                            </p>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Totals */}
                            <div className="bg-green-50 rounded-lg p-4">
                                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                                    <DollarSign size={18} />
                                    Total
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(totals.subtotal)}</span>
                                    </div>
                                    {totals.backbar > 0 && (
                                        <div className="flex justify-between text-gray-600">
                                            <span>Backbar</span>
                                            <span>{formatCurrency(totals.backbar)}</span>
                                        </div>
                                    )}
                                    {totals.tax > 0 && (
                                        <div className="flex justify-between text-gray-600">
                                            <span>Tax</span>
                                            <span>{formatCurrency(totals.tax)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg font-semibold text-green-800 pt-2 border-t border-green-200">
                                        <span>Total</span>
                                        <span>{formatCurrency(totals.total)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            No appointment data available
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t px-6 py-4 bg-gray-50 space-y-3">
                    {/* Payment Status Messages */}
                    {paymentState === 'success' && paymentResult && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-green-800">Payment Successful!</p>
                                <p className="text-sm text-green-700 mt-1">
                                    Charged: ${(paymentResult.totalCents / 100).toFixed(2)}
                                    {paymentResult.tipCents > 0 && (
                                        <span className="ml-2">(includes ${(paymentResult.tipCents / 100).toFixed(2)} tip)</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Upcoming Appointments */}
                    {paymentState === 'success' && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Calendar className="text-purple-600" size={20} />
                                    <p className="font-medium text-purple-800">Upcoming Appointments</p>
                                </div>
                                {autoCloseCountdown !== null && (
                                    <span className="text-xs text-purple-500">
                                        Auto-close in {autoCloseCountdown}s
                                    </span>
                                )}
                            </div>
                            {upcomingLoading ? (
                                <div className="flex items-center gap-2 text-purple-700">
                                    <Loader2 className="animate-spin" size={16} />
                                    <span className="text-sm">Loading upcoming appointments...</span>
                                </div>
                            ) : upcomingAppointments.length > 0 ? (
                                <div className="space-y-2">
                                    {upcomingAppointments.map((appt, index) => (
                                        <div key={appt.ticket_id || index} className="bg-white rounded-md p-3 border border-purple-100">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-medium text-gray-800">{appt.pet_name}</p>
                                                    <p className="text-sm text-gray-600">{appt.item_description}</p>
                                                    <p className="text-sm text-gray-500">with {appt.employee_name}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-medium text-purple-700">
                                                        {new Date(appt.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        {new Date(appt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-purple-700">No upcoming appointments scheduled.</p>
                            )}
                            
                            {/* Book Appointment Button */}
                            <button
                                onClick={() => {
                                    clearAutoCloseTimers();
                                    window.open('https://zoomgroom.saloniris.com/booking', '_blank');
                                }}
                                className="mt-4 w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <CalendarPlus size={18} />
                                Book Appointment
                            </button>
                        </div>
                    )}

                    {paymentState === 'error' && paymentError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                            <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-red-800">Payment Failed</p>
                                <p className="text-sm text-red-700 mt-1">{paymentError}</p>
                            </div>
                        </div>
                    )}

                    {paymentState === 'processing' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                            <Loader2 className="text-blue-600 animate-spin" size={20} />
                            <div>
                                <p className="font-medium text-blue-800">Processing Payment...</p>
                                <p className="text-sm text-blue-700">Waiting for customer to complete payment on terminal</p>
                            </div>
                        </div>
                    )}

                    {/* Server Health Error */}
                    {serverHealthy === false && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
                            <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-orange-800">Payment Server Unavailable</p>
                                <p className="text-sm text-orange-700 mt-1">{serverError || 'Cannot connect to Square payments server'}</p>
                            </div>
                        </div>
                    )}

                    {/* Pay Now Button - hidden in view-only mode */}
                    {!viewOnly && ticket && hasTerminal && paymentState !== 'success' && (
                        <button
                            onClick={handlePayNow}
                            disabled={paymentState === 'processing' || totals.total <= 0 || serverHealthy === false}
                            className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                                paymentState === 'processing' || totals.total <= 0 || serverHealthy === false
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                        >
                            <CreditCard size={20} />
                            {serverHealthy === null ? 'Checking server...' : 
                             paymentState === 'processing' ? 'Processing...' : 
                             `Pay Now - ${formatCurrency(totals.total)}`}
                        </button>
                    )}

                    {/* Skip Payment Button - only for user_id 153, hidden in view-only mode */}
                    {!viewOnly && ticket && currentUserId === 153 && paymentState !== 'success' && paymentState !== 'processing' && (
                        <button
                            onClick={handleSkipPayment}
                            className="w-full py-2 px-4 rounded-lg font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 transition-colors"
                        >
                            Skip Payment
                        </button>
                    )}

                    {/* No terminal warning - hidden in view-only mode */}
                    {!viewOnly && ticket && !hasTerminal && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                            No Square terminal configured for this location
                        </div>
                    )}

                    {/* Cancel Appointment Button - shown when onCancelAppointment is provided and not already paid/cancelled */}
                    {onCancelAppointment && ticket && cancelState !== 'success' && paymentState !== 'success' && (
                        !showCancelConfirm ? (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                className="w-full py-2 px-4 rounded-lg font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                            >
                                <XCircle size={18} />
                                Cancel Appointment
                            </button>
                        ) : (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                                    <p className="font-medium text-red-800">Are you sure you want to cancel this appointment?</p>
                                </div>
                                <textarea
                                    value={cancelComment}
                                    onChange={(e) => setCancelComment(e.target.value)}
                                    placeholder="Cancellation reason (optional)"
                                    className="w-full border border-red-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                                    rows={2}
                                />
                                {cancelState === 'error' && cancelError && (
                                    <p className="text-sm text-red-700">{cancelError}</p>
                                )}
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleCancelAppointment}
                                        disabled={cancelState === 'cancelling'}
                                        className={`flex-1 py-2 px-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 transition-colors ${
                                            cancelState === 'cancelling'
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                    >
                                        {cancelState === 'cancelling' ? (
                                            <><Loader2 className="animate-spin" size={16} /> Cancelling…</>
                                        ) : (
                                            'Yes, Cancel Appointment'
                                        )}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowCancelConfirm(false);
                                            setCancelComment('');
                                            setCancelState('idle');
                                            setCancelError(null);
                                        }}
                                        disabled={cancelState === 'cancelling'}
                                        className="flex-1 py-2 px-3 rounded-lg font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                                    >
                                        No, Keep It
                                    </button>
                                </div>
                            </div>
                        )
                    )}

                    {/* Cancel success message */}
                    {cancelState === 'success' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-green-800">Appointment Cancelled</p>
                                <p className="text-sm text-green-700 mt-1">The appointment has been successfully cancelled.</p>
                            </div>
                        </div>
                    )}

                    {/* Close Button */}
                    <button
                        onClick={handleClose}
                        className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AppointmentDetailsModal;
